import { ContractOutOfDefaultFieldResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import {
  PartnerContractPartyType,
  PartnerContractStatus,
} from '@interfaces/partners/contracts/responses/partner-contract.response';
import { PartnerContractRevisionAction } from '@interfaces/partners/contracts/responses/partner-contract-revision.response';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';

/** Token de variável no HTML, mesma regra do backend (`PartnerContractVariables`). */
const VARIABLE_TOKEN = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/**
 * A minuta está finalizada (texto travado).
 *
 * O backend serializa enum como string (`JsonStringEnumConverter` global); o número fica aceito
 * por segurança, caso o endpoint um dia responda sem o conversor.
 */
export function isPartnerContractFinalized(
  status: PartnerContractStatus | number | null | undefined
): boolean {
  return status === 'Finalized' || status === 1;
}

export function partnerContractStatusLabel(
  status: PartnerContractStatus | number | null | undefined
): string {
  return isPartnerContractFinalized(status) ? 'Finalizado' : 'Rascunho';
}

/** "Pessoa jurídica" / "Pessoa física" (`short`: "PJ" / "PF", para a coluna da lista). */
export function partnerContractPartyTypeLabel(
  partyType: PartnerContractPartyType | null | undefined,
  short = false
): string {
  const individual = partyType === 'Individual';
  if (short) return individual ? 'PF' : 'PJ';
  return individual ? 'Pessoa física' : 'Pessoa jurídica';
}

const REVISION_ACTION_LABELS: Record<PartnerContractRevisionAction, string> = {
  Created: 'Criado',
  Edited: 'Editado',
  VariablesRefilled: 'Variáveis preenchidas',
  Finalized: 'Finalizado',
  Reopened: 'Reaberto',
  TextEdited: 'Texto alterado',
};

/**
 * Aviso para quem não está na lista de editores do texto (card 3643). Mesmo texto da recusa do
 * backend, para a pessoa ler a mesma orientação na tela e no erro.
 */
export const CONTRACT_TEXT_LOCKED_MESSAGE =
  'Só quem está na lista de editores do texto dos contratos (Parametrização › Comercial) pode alterar as cláusulas. Ajuste os campos do contrato ou peça a alteração a um editor.';

/**
 * Número do contrato para exibir (card 3643): `contractNumber` (`PR-202610-500`), com o
 * `contractCode` de reserva enquanto o backend antigo ainda responde.
 */
export function partnerContractNumber(
  contract: { contractNumber?: string | null; contractCode?: string | null } | null | undefined
): string {
  return contract?.contractNumber || contract?.contractCode || '';
}

/**
 * Percentual vindo do backend como número ou texto invariante ("15.00") para a tela: "15%",
 * "1,5%". Vazio vira "-". Só formata; nenhum cálculo.
 */
export function formatContractPercent(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(parsed)) return String(value);
  return `${parsed.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
}

/** "Regra geral 15% (padrão 10%); Crédito já aprovado 25% (padrão 20%)", para o aviso de aprovação. */
export function describeOutOfDefaultFields(
  fields: ContractOutOfDefaultFieldResponse[] | null | undefined
): string {
  return (fields ?? [])
    .map(
      field =>
        `${field.label} ${formatContractPercent(field.currentValue)} (padrão ${formatContractPercent(field.defaultValue)})`
    )
    .join('; ');
}

/** Ação da revisão da minuta em português ("Variáveis preenchidas"). */
export function partnerContractRevisionActionLabel(
  action: PartnerContractRevisionAction | string | null | undefined
): string {
  return REVISION_ACTION_LABELS[action as PartnerContractRevisionAction] ?? String(action ?? '');
}

/** Rótulos das variáveis pendentes, para o aviso ("CNPJ da parceira, Endereço da parceira"). */
export function describeUnfilledVariables(keys: string[], variables: RichTextVariable[]): string {
  return keys.map(key => variables.find(v => v.key === key)?.label ?? key).join(', ');
}

/** Destaca, no HTML da pré-visualização, as variáveis que ainda estão sem valor. */
export function highlightUnfilledVariables(html: string, variables: RichTextVariable[]): string {
  return (html ?? '').replace(VARIABLE_TOKEN, (token, key: string) => {
    const label = variables.find(v => v.key === key)?.label ?? key;
    return `<span class="contract-unfilled" title="Falta preencher: ${escapeAttribute(label)}">${token}</span>`;
  });
}

/**
 * Remove do HTML o que não tem lugar num documento (script, iframe, handlers `on*`, `javascript:`).
 *
 * O backend já sanitiza ao gravar; isto é a segunda camada da pré-visualização, que mostra o texto
 * ainda **não salvo** do editor com `bypassSecurityTrustHtml` (o sanitizador do Angular apagaria as
 * cores e o alinhamento, que vêm em `style` e `class`).
 */
export function stripUnsafeHtml(html: string): string {
  if (typeof DOMParser === 'undefined') return '';

  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  doc
    .querySelectorAll(
      'script, style, iframe, object, embed, link, meta, img, svg, math, form, base, video, audio, source, noscript, template, textarea, frame, frameset'
    )
    .forEach(node => node.remove());
  doc.body.querySelectorAll('*').forEach(element => {
    Array.from(element.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase();
      // Tira espaço e caractere de controle antes de olhar o esquema: "java\tscript:" passaria.
      const value = attribute.value.replace(/[\s\u0000-\u001f]/g, '').toLowerCase();
      const unsafeLink =
        (name === 'href' || name === 'src' || name === 'action') &&
        !/^(https?:|mailto:|#)/.test(value);
      if (name.startsWith('on') || unsafeLink) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  return doc.body.innerHTML;
}

/** Mensagem do backend (`ErrorResponse.errors[0].message`), ou vazio para o toast usar só o título. */
export function backendErrorMessage(error: unknown): string {
  const errors = (error as { errors?: { message?: string }[] } | null)?.errors;
  return errors?.length ? (errors[0].message ?? '') : '';
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
